/**
 * Provider plumbing for the API client.
 *
 * Lives in a side-effect-free module — separate from ``client.ts`` —
 * so callers that need to register providers (the scope-override hook
 * is the canonical one) don't pull ``client.ts`` into the import graph
 * at app boot. ``client.ts`` reads ``VITE_API_BASE_URL`` at module load
 * and throws if it's missing, which would break the e2e mock-mode
 * build where no API URL is set.
 *
 * ``client.ts`` re-exports the public functions here so consumers can
 * continue to ``import { setAuthTokenProvider } from "./client"`` — only
 * the side-effect-free chain of registration callers (e.g. the
 * scope-override hook) needs to point at ``./providers`` directly.
 */

type TokenProvider = () => Promise<string | null>;
type ExtraHeadersProvider = () => Record<string, string>;

let _tokenProvider: TokenProvider = async () => null;
let _extraHeadersProvider: ExtraHeadersProvider = () => ({});

/** Register the function the API client should call to obtain an auth token. */
export function setAuthTokenProvider(provider: TokenProvider): void {
  _tokenProvider = provider;
}

/**
 * Register a function the API client calls on every request to inject extra
 * headers. Used by the scope-override toggle to send ``X-Scope-Override: all``
 * when the user has flipped "Show all customers" in the account menu.
 *
 * Returned headers are merged into the request after the caller's per-request
 * headers but before the Authorization header in ``buildRequestHeaders``, so
 * Authorization always wins on collision and can't be forged from this surface.
 */
export function setExtraHeadersProvider(provider: ExtraHeadersProvider): void {
  _extraHeadersProvider = provider;
}

/** Fetch the current auth token from the registered provider (or null). */
export async function getAuthToken(): Promise<string | null> {
  return _tokenProvider();
}

/**
 * Read the registered extra-headers. Used by ``client.ts::request`` per
 * call. Exported for tests that need to assert what the registered
 * provider returns without going through the API client.
 */
export function getExtraHeaders(): Record<string, string> {
  return _extraHeadersProvider();
}

/** A request the server refused, as the rejection handler sees it. */
export interface RejectedRequest {
  status: number;
  /** The request path as the caller wrote it (no base URL). */
  path: string;
  /** The parsed JSON body when the server sent one, else null. */
  body: unknown;
}

type RejectedRequestHandler = (rejected: RejectedRequest) => void;

let _rejectedRequestHandler: RejectedRequestHandler | null = null;

/**
 * Register a function the API client calls for every non-2xx answer, before the caller's own
 * error handling runs. The customer portal uses it to react to the two answers that mean "who you
 * are working as is no longer valid" (a 401, or the 409 `choose_customer`) in one place instead
 * of in every page. Pass `null` to remove it. No other shell registers one.
 */
export function setRejectedRequestHandler(
  handler: RejectedRequestHandler | null,
): void {
  _rejectedRequestHandler = handler;
}

/** Called by ``client.ts`` for each non-2xx response. A throwing handler never masks the API error. */
export function notifyRejectedRequest(rejected: RejectedRequest): void {
  try {
    _rejectedRequestHandler?.(rejected);
  } catch (err) {
    console.error("[api] rejected-request handler failed:", err);
  }
}

/**
 * Supplies a bearer token for the feedback hub (production's API) when quick notes are sent across environments
 * (feedback quick capture spec 01 §3). Resolves null when this shell has no way to sign in for it; rejects with an
 * `Error` whose message says why (sign-in cancelled, hub scope not configured).
 */
type FeedbackHubTokenProvider = () => Promise<string | null>;

let _feedbackHubTokenProvider: FeedbackHubTokenProvider = async () => null;

/** Register how the shell obtains a hub token. The web shell registers it at boot when a hub is configured. */
export function setFeedbackHubTokenProvider(
  provider: FeedbackHubTokenProvider,
): void {
  _feedbackHubTokenProvider = provider;
}

/** The hub token from the registered provider (or null). */
export async function getFeedbackHubToken(): Promise<string | null> {
  return _feedbackHubTokenProvider();
}
